import React, { FC } from 'react';
import { Row, Col } from 'antd';
import styled from 'styled-components';
import { ManchetContentCamptonBook22Grey } from '../../../../layout/globalFonts';
import { StyledTableLabel } from '../../../../layout/newGlobalFonts';
import ContentStore from '../../../../stores/ContentStore';
import LoadingSpinner from '../../../../common-components/LoadingSpinner';
import decimalFormatting from '../../../../utils/decimalFormatting';
import { Typography } from '../../../../layout/components/Typography';
import { ColorScheme } from '../../../../layout/colorScheme';
import { AgreementDisplayRow } from './AgreementTypes';

const StyledText = styled(ManchetContentCamptonBook22Grey)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
`;

const MobileAgreementRow = styled(Row)<{ $isEven?: boolean }>`
  background-color: ${(props) => (props.$isEven ? 'white' : '#f7f7f7')};
  margin: 0;
  padding: 18px 20px;
  box-sizing: border-box;
  border-bottom: none;
`;

const TableTitleRow = styled(Row)`
  padding: 16px 18px;
  min-height: 70px;
`;

const HeaderFlexEndColumn = styled(Col)`
  display: flex;
  justify-content: flex-end;
  padding-right: 0 !important;
  padding-left: 10px !important;
`;

const BodyFlexEndColumn = styled(Col)`
  display: flex;
  justify-content: flex-end;
`;

interface AgreementsTableMobileProps {
  contentStore: ContentStore;
  rows: AgreementDisplayRow[];
  isLoading: boolean;
}

const AgreementsTableMobile: FC<AgreementsTableMobileProps> = ({
  contentStore,
  rows,
  isLoading
}) => {
  const renderBody = () => {
    if (isLoading) {
      return (
        <Row justify="center" align="middle" style={{ padding: '40px 0' }}>
          <LoadingSpinner />
        </Row>
      );
    }

    if (rows.length === 0) {
      return (
        <StyledText>{contentStore.cmsStore.getContent('NoAgreements')?.content || ''}</StyledText>
      );
    }

    return rows.map((agreement, index) => (
      <MobileAgreementRow
        key={agreement.key}
        gutter={12}
        align="middle"
        style={{ marginLeft: 0, marginRight: 0 }}
        $isEven={index % 2 !== 0}
      >
        <Col span={5}>
          <StyledTableLabel>{agreement.nextPayment}</StyledTableLabel>
        </Col>
        <Col span={9}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <Typography as="p" variant="contentSmallLink" align="left" style={{ margin: 0 }}>
              {agreement.description.includes('ØresundGO')
                ? contentStore.cmsStore.getContent('DiscountAgreementName')?.content
                : contentStore.cmsStore.getContent('DiscountAgreementName')?.tooltip}
            </Typography>
            <Typography
              as="p"
              variant="contentSmall"
              color={ColorScheme.Grey6}
              align="left"
              style={{ margin: 0 }}
            >
              {contentStore.cmsStore.getContent('DiscountAgreementName')?.subText}
            </Typography>
          </div>
        </Col>
        <BodyFlexEndColumn span={10} style={{ paddingRight: 0 }}>
          <Typography
            variant="contentSmall"
            as="span"
            align="right"
            color={ColorScheme.Grey7}
            style={{ margin: 0 }}
          >
            {decimalFormatting(agreement.amount)} {agreement.currency}
          </Typography>
        </BodyFlexEndColumn>
      </MobileAgreementRow>
    ));
  };

  return (
    <Col span={24}>
      <TableTitleRow align="middle">
        <Col span={5} style={{ paddingLeft: '5px', paddingRight: '10px' }}>
          <StyledTableLabel $color={ColorScheme.Grey6}>
            {contentStore.cmsStore.getContent('NextPayment')?.content}
          </StyledTableLabel>
        </Col>
        <Col span={10} style={{ paddingLeft: '10px' }}>
          <StyledTableLabel $color={ColorScheme.Grey6}>
            {contentStore.cmsStore.getContent('DiscountAgreement')?.content}
          </StyledTableLabel>
        </Col>
        <HeaderFlexEndColumn span={9}>
          <StyledTableLabel $color={ColorScheme.Grey6}>
            {contentStore.cmsStore.getContent('Price')?.content}
          </StyledTableLabel>
        </HeaderFlexEndColumn>
      </TableTitleRow>
      {renderBody()}
    </Col>
  );
};

export default AgreementsTableMobile;
